import { useEffect, useState } from "react";

import { ANIMATION_TIMINGS } from "../constants";
import { type ScoreChange } from "../types";

interface AnimationState {
  /**
   * Index of the current score change being animated (-1 means not started)
   */
  currentChangeIndex: number;
  /**
   * Whether the score counter is currently animating
   */
  isScoreAnimating: boolean;
  /**
   * Number of line items that should be visible
   */
  visibleLineItemCount: number;
  /**
   * Whether all animations have completed and button should be enabled
   */
  isAnimationComplete: boolean;
}

interface UseScoreAnimationSequenceResult {
  /**
   * The current end value for the score counter
   */
  currentEndScore: number;
  /**
   * Whether the score counter is currently animating
   */
  isScoreAnimating: boolean;
  /**
   * Number of line items that should be visible (0 = none, 1 = first, etc.)
   */
  visibleLineItemCount: number;
  /**
   * Whether the button should be enabled
   */
  isButtonEnabled: boolean;
}

export function useScoreAnimationSequence(
  startScore: number,
  scoreChanges: ScoreChange[]
): UseScoreAnimationSequenceResult {
  const [animationState, setAnimationState] = useState<AnimationState>({
    currentChangeIndex: -1,
    isScoreAnimating: false,
    visibleLineItemCount: 0,
    isAnimationComplete: false,
  });

  // Schedules all animation timers upfront with absolute delays (flat scheduling).
  // This avoids nested timer chains (where one callback schedules the next), which
  // cause infinite loops with fake timers in tests.
  //
  // Timeline for N score changes:
  //   [INITIAL_DELAY] → start(0) → [DURATION + LINE_ITEM_DELAY] → reveal(0)
  //     → [BETWEEN_CHANGES_DELAY] → start(1) → [DURATION + LINE_ITEM_DELAY] → reveal(1)
  //     → ... repeat for each score change
  useEffect(() => {
    // Nothing to animate — mark complete immediately.
    // Returns same reference when already complete to prevent infinite re-renders
    // if the caller passes an unstable [] reference on each render.
    if (scoreChanges.length === 0) {
      setAnimationState((previous) =>
        previous.isAnimationComplete
          ? previous
          : {
              currentChangeIndex: -1,
              isScoreAnimating: false,
              visibleLineItemCount: 0,
              isAnimationComplete: true,
            }
      );
      return;
    }

    const timers: NodeJS.Timeout[] = [];

    function scheduleTimer(callback: () => void, delay: number): void {
      const timer = setTimeout(callback, delay);
      timers.push(timer);
    }

    // Pre-calculate and schedule all timers with absolute delays
    let cumulativeDelay = ANIMATION_TIMINGS.INITIAL_DELAY_MS;

    for (let index = 0; index < scoreChanges.length; index++) {
      // Add gap between consecutive score changes (not before the first one)
      if (index > 0) {
        cumulativeDelay += ANIMATION_TIMINGS.BETWEEN_CHANGES_DELAY_MS;
      }

      // Step 1: Start the score counter animation for this change
      const startAnimationDelay = cumulativeDelay;
      scheduleTimer(() => {
        setAnimationState((previous) => ({
          ...previous,
          currentChangeIndex: index,
          isScoreAnimating: true,
        }));
      }, startAnimationDelay);

      cumulativeDelay +=
        ANIMATION_TIMINGS.SCORE_ANIMATION_DURATION_MS + ANIMATION_TIMINGS.LINE_ITEM_DELAY_MS;

      // Step 2: Stop animating and reveal the line item
      const showLineItemDelay = cumulativeDelay;
      const isLastChange = index === scoreChanges.length - 1;
      scheduleTimer(() => {
        setAnimationState((previous) => ({
          ...previous,
          isScoreAnimating: false,
          visibleLineItemCount: index + 1,
          ...(isLastChange && { isAnimationComplete: true }),
        }));
      }, showLineItemDelay);
    }

    return () => {
      timers.forEach((timer) => {
        clearTimeout(timer);
      });
    };
  }, [scoreChanges]);

  // Calculate current end score based on animation state
  const currentEndScore =
    animationState.currentChangeIndex < 0
      ? startScore
      : scoreChanges
          .slice(0, animationState.currentChangeIndex + 1)
          .reduce((sum, change) => sum + change.value, startScore);

  return {
    currentEndScore,
    isScoreAnimating: animationState.isScoreAnimating,
    visibleLineItemCount: animationState.visibleLineItemCount,
    isButtonEnabled: animationState.isAnimationComplete,
  };
}
